import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  IdCard,
  Banknote,
  ShieldCheck,
  Truck,
  Camera,
  BadgeCheck,
  Wallet,
  CalendarClock,
  CalendarCheck,
  Car,
  Flag,
  Check,
} from "lucide-react";

/**
 * CockpitTimeline — timeline verticale 13 étapes façon cockpit Tesla.
 * L'étape actuelle est agrandie et signalée en accent lime.
 * Les étapes précédentes portent un ✓ minimal, les suivantes sont dimmed.
 */
export const ETAPES_WYVIO = [
  { key: 1, label: "Dossier créé", icon: FileText },
  { key: 2, label: "Documents client", icon: IdCard },
  { key: 3, label: "Financement validé", icon: BadgeCheck },
  { key: 4, label: "Acompte reçu", icon: Banknote },
  { key: 5, label: "Commande fournisseur", icon: Truck },
  { key: 6, label: "Véhicule en préparation", icon: Car },
  { key: 7, label: "Photos validées", icon: Camera },
  { key: 8, label: "Contrôle qualité", icon: ShieldCheck },
  { key: 9, label: "Solde reçu", icon: Wallet },
  { key: 10, label: "Créneaux proposés", icon: CalendarClock },
  { key: 11, label: "Créneau confirmé", icon: CalendarCheck },
  { key: 12, label: "Livraison en cours", icon: Truck },
  { key: 13, label: "Livraison confirmée", icon: Flag },
];

export function CockpitTimeline({
  etapes = ETAPES_WYVIO,
  current = 10,
  className = "",
  testid,
}) {
  return (
    <div data-testid={testid} className={`relative ${className}`}>
      {/* Ligne verticale de fond */}
      <div
        aria-hidden
        className="absolute left-[17px] top-4 bottom-4 w-px"
        style={{ background: "#EEEEE8" }}
      />

      <ul className="relative space-y-2.5">
        {etapes.map((e, i) => {
          const done = e.key < current;
          const active = e.key === current;
          const upcoming = e.key > current;
          const Icon = e.icon;

          return (
            <motion.li
              key={e.key}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.03 * i + 0.1,
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="relative flex items-center gap-4"
              data-testid={`etape-${e.key}`}
            >
              {/* Marker */}
              <div
                className={`relative z-10 flex flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                  active ? "wyvio-segment-active" : ""
                } ${active ? "h-12 w-12" : "h-9 w-9"}`}
                style={
                  active
                    ? {
                        background: "#C8FF2F",
                        color: "#151515",
                        boxShadow:
                          "0 0 0 5px #F8F8F5, 0 0 0 6px rgba(200,255,47,0.35), 0 8px 24px -8px rgba(200,255,47,0.5)",
                      }
                    : done
                      ? {
                          background: "transparent",
                          color: "#B8B8B0",
                          boxShadow: "inset 0 0 0 1px #E8E8E2",
                        }
                      : {
                          background: "transparent",
                          color: "#D9D9D2",
                          boxShadow: "inset 0 0 0 1px #EEEEE8",
                        }
                }
              >
                {done ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={2} />
                ) : (
                  <Icon
                    className={active ? "h-4 w-4" : "h-3.5 w-3.5"}
                    strokeWidth={active ? 2 : 1.6}
                  />
                )}
              </div>

              {/* Label */}
              <div
                className={`flex flex-1 items-center justify-between rounded-[14px] py-2 pl-3 pr-3 transition-colors ${
                  active ? "bg-white shadow-wyvio-elev" : ""
                }`}
              >
                <div>
                  {active && (
                    <p className="text-[9.5px] font-medium uppercase tracking-[0.24em] text-[#8A8A80]">
                      Étape en cours
                    </p>
                  )}
                  <p
                    className={`${
                      active
                        ? "text-[16px] font-medium text-[#151515]"
                        : done
                          ? "text-[13px] font-normal text-[#8A8A80]"
                          : "text-[13px] font-normal text-[#C4C4B8]"
                    } tracking-[-0.01em]`}
                  >
                    {e.label}
                  </p>
                </div>
                <span
                  className={`wyvio-tabular text-[10.5px] font-medium ${
                    active
                      ? "text-[#151515]"
                      : done
                        ? "text-[#B8B8B0]"
                        : "text-[#D9D9D2]"
                  }`}
                >
                  {String(e.key).padStart(2, "0")}
                </span>
              </div>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}
